import React, { useContext, Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, AuthContext } from "./Common/Components/AuthContext";

// website routes (eagerly loaded — landing page entry point)
import WebsiteLayout from "./Website/Components/WebsiteLayout";
import Home from "./Website/Pages/Home";
import About from "./Website/Pages/About";
import Pricing from "./Website/Pages/Pricing";
import Contact from "./Website/Pages/Contact";
import RefundPolicy from "./Website/Pages/RefundPolicy";
import TermsAndConditions from "./Website/Pages/TermsAndConditions";
import PrivacyPolicy from "./Website/Pages/PrivacyPolicy";
import ShippingPolicy from "./Website/Pages/ShippingPolicy";

// additional components
import ScrollToTop from "./Website/Components/ScrollToTop";
import ProtectedRoute from "./ProtectedRoute";

// additional pages
import PageNotFound from "./Website/Components/PageNotFound";

// common pages
import Register from "./Common/Components/Register";
import LoadingSpinner from "./Common/Components/LoadingSpinner";

// customer panel routes (lazy loaded)
const CustomerLayout = lazy(() => import("./Customer/Components/CustomerLayout"));
const CustomerLogin = lazy(() => import("./Customer/Components/CustomerLogin"));
const CustomerHome = lazy(() => import("./Customer/Pages/CustomerHome"));
const CustomerProfile = lazy(() => import("./Customer/Pages/CustomerProfile"));
const CustomerSubscription = lazy(() => import("./Customer/Pages/CustomerSubscription"));
const CustomerCardDetails = lazy(() => import("./Customer/Pages/CustomerCardDetails"));
const CustomerFlipkart = lazy(() => import("./Customer/Pages/CustomerFlipkart"));
const CustomerFlipkartOrders = lazy(() => import("./Customer/Pages/CustomerFlipkartOrders"));
const CustomerFlipkartAccountData = lazy(() => import("./Customer/Pages/CustomerFlipkartAccountData"));
const CustomerFlipkartStats = lazy(() => import("./Customer/Pages/CustomerFlipkartStats"));
const CustomerAutoOrder = lazy(() => import("./Customer/Pages/CustomerAutoOrder"));
const CustomerAutoOrders = lazy(() => import("./Customer/Pages/CustomerAutoOrders"));
const CustomerStockAnalyser = lazy(() => import("./Customer/Pages/CustomerStockAnalyser"));
const CustomerStockAnalyserAdd = lazy(() => import("./Customer/Pages/CustomerStockAnalyserAdd"));
const CustomerStockAnalyserAlerts = lazy(() => import("./Customer/Pages/CustomerStockAnalyserAlerts"));
const CustomerStockAnalyserSettings = lazy(() => import("./Customer/Pages/CustomerStockAnalyserSettings"));
const CustomerStaff = lazy(() => import("./Customer/Pages/CustomerStaff"));
const CustomerSubscriptions = lazy(() => import("./Customer/Pages/CustomerSubscriptions"));
const CustomerHdfcCollection = lazy(() => import("./Customer/Pages/CustomerHdfcCollection"));

// admin panel routes (lazy loaded)
const AdminLogin = lazy(() => import("./Admin/Components/AdminLogin"));
const AdminLayout = lazy(() => import("./Admin/Components/AdminLayout"));
const AdminHome = lazy(() => import("./Admin/Pages/AdminHome"));
const AdminProfile = lazy(() => import("./Admin/Pages/AdminProfile"));
const Customers = lazy(() => import("./Admin/Pages/Customers"));

// superadmin panel routes (lazy loaded)
const SuperAdminLayout = lazy(() => import("./SuperAdmin/Components/SuperAdminLayout"));
const SuperAdminHome = lazy(() => import("./SuperAdmin/Pages/SuperAdminHome"));
const SuperAdminLogin = lazy(() => import("./SuperAdmin/Components/SuperAdminLogin"));
const Agents = lazy(() => import("./SuperAdmin/Pages/Agents"));
const FlipkartManager = lazy(() => import("./SuperAdmin/Pages/FlipkartManager"));
const SAManager = lazy(() => import("./SuperAdmin/Pages/SAManager"));
const AutoOrderManager = lazy(() => import("./SuperAdmin/Pages/AutoOrderManager"));
const AutoOrderDashboard = lazy(() => import("./SuperAdmin/Pages/AutoOrderDashboard"));
const SubscriptionManager = lazy(() => import("./SuperAdmin/Pages/SubscriptionManager"));
const SuperAdminProfile = lazy(() => import("./SuperAdmin/Pages/SuperAdminProfile"));
const SystemMonitor = lazy(() => import("./SuperAdmin/Pages/SystemMonitor"));
const HdfcCollectionManager = lazy(() => import("./SuperAdmin/Pages/HdfcCollectionManager"));
const VPRManager = lazy(() => import("./SuperAdmin/Pages/VPRManager"));
const ScanPackManager = lazy(() => import("./SuperAdmin/Pages/ScanPackManager"));

// wrapper component for login redirects
const LoginWrapper = ({ children, isAuthenticated, redirectTo }) => {
  return isAuthenticated ? <Navigate to={redirectTo} replace /> : children;
};

const AppRoutes = () => {
  const {
    isUserAuthenticated,
    isAdminAuthenticated,
    isSuperAdminAuthenticated,
  } = useContext(AuthContext);

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public website  */}
          <Route element={<WebsiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
            <Route path="/refundpolicy" element={<RefundPolicy />} />
            <Route path="/termsandconditions" element={<TermsAndConditions />} />
            <Route path="/shippingpolicy" element={<ShippingPolicy />} />
            <Route path="/register" element={<Register />} />
            {/* Customer login - redirect to /crm if already authenticated  */}
            {
              <Route
                path="/login"
                element={
                  <LoginWrapper
                    isAuthenticated={isUserAuthenticated}
                    redirectTo="/crm"
                  >
                    <CustomerLogin />
                  </LoginWrapper>
                }
              />
            }
            {/* Admin Login - Redirect to /admin if already authenticated  */}
            <Route
              path="/admin/login"
              element={
                <LoginWrapper
                  isAuthenticated={isAdminAuthenticated}
                  redirectTo="/admin"
                >
                  <AdminLogin />
                </LoginWrapper>
              }
            />
            {/* SuperAdmin Login - Redirect to /superadmin if already authenticated  */}
            <Route
              path="/superadmin/login"
              element={
                <LoginWrapper
                  isAuthenticated={isSuperAdminAuthenticated}
                  redirectTo="/superadmin"
                >
                  <SuperAdminLogin />
                </LoginWrapper>
              }
            />
            {/* Catch-all for 404 - must be last in this Route group */}
            <Route path="*" element={<PageNotFound />} />
          </Route>
          <Route path="/crm" element={<ProtectedRoute role="user" />}>
            <Route element={<CustomerLayout />}>
              <Route index element={<CustomerHome />} />
              <Route path="subscription" element={<CustomerSubscription />} />
              <Route path="subscriptions" element={<CustomerSubscriptions />} />
              <Route path="profile" element={<CustomerProfile />} />
              <Route path="carddetails" element={<CustomerCardDetails />} />
              <Route path="flipkart" element={<CustomerFlipkart />} />
              <Route path="flipkart/orders" element={<CustomerFlipkartOrders />} />
              <Route path="flipkart/accountdata" element={<CustomerFlipkartAccountData />} />
              <Route path="flipkart/stats" element={<CustomerFlipkartStats />} />
              <Route path="autoorder" element={<CustomerAutoOrder />} />
              <Route path="autoorder/orders" element={<CustomerAutoOrders />} />
              <Route path="stockanalyser" element={<CustomerStockAnalyser />} />
              <Route path="stockanalyser/add" element={<CustomerStockAnalyserAdd />} />
              <Route path="stockanalyser/alerts" element={<CustomerStockAnalyserAlerts />} />
              <Route path="stockanalyser/settings" element={<CustomerStockAnalyserSettings />} />
              <Route path="staff" element={<CustomerStaff />} />
              <Route path="hdfc-collection" element={<CustomerHdfcCollection />} />
            </Route>
          </Route>

          {/* Admin Panel  */}
          {/* Admin Protected Routes */}
          <Route path="/admin" element={<ProtectedRoute role="admin" />}>
            <Route element={<AdminLayout />}>
              <Route index element={<AdminHome />} />
              <Route path="customers" element={<Customers />} />
              <Route path="profile" element={<AdminProfile />} />
            </Route>
          </Route>

          {/* superadmin panel  */}
          {/* SuperAdmin Protected Routes */}
          <Route
            path="/superadmin"
            element={<ProtectedRoute role="superadmin" />}
          >
            <Route element={<SuperAdminLayout />}>
              <Route index element={<SuperAdminHome />} />
              <Route path="agents" element={<Agents />} />
              <Route path="flipkart" element={<FlipkartManager />} />
              <Route path="sa-manager" element={<SAManager />} />
              <Route path="auto-order" element={<AutoOrderManager />} />
              <Route path="auto-order-dashboard" element={<AutoOrderDashboard />} />
              <Route path="subscriptions" element={<SubscriptionManager />} />
              <Route path="profile" element={<SuperAdminProfile />} />
              <Route path="monitor" element={<SystemMonitor />} />
              <Route path="hdfc-collection" element={<HdfcCollectionManager />} />
              <Route path="vpr-manager" element={<VPRManager />} />
              <Route path="scanpack-manager" element={<ScanPackManager />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => {
  return (
    <>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </>
  );
};

export default App;
